import { Color } from '@engineering11/ui-lib/e11-ui-config'
import { HasId, ITimestamps } from '@engineering11/types'
import { IClientToolCall } from './assistant-client-tool.model'

/**
 * Frontend contract types for the employer assistant surface (Epic 4, story 4.2).
 *
 * Backend-shaped values (the exact string values below, and the field names on the
 * conversation/message/run-step documents) are ASSUMPTIONS mirrored from the already-merged
 * backend assistant epics. They are confined to this file, the repository, the service and the
 * `ASSISTANT_CONVERSATION` collection constant. Confirm/replace them per BACKEND-CONTRACT.md.
 */

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * Conversation lifecycle status. Only `Idle` permits a send; every other value means the
 * conversation is busy and the composer send control is disabled (CAP-3).
 */
export enum ConversationStatus {
  Idle = 'idle',
  Running = 'running',
  AwaitingConfirmation = 'awaiting_confirmation',
  AwaitingClient = 'awaiting_client',
}

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * Per-run-step status. The four values below are the contract's core visual pills
 * (design-contract.md → §Surface 2). Unknown/extra backend statuses map to a neutral pill
 * via `runStepStatusToTag`.
 */
export enum RunStepStatus {
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  AwaitingClient = 'awaiting_client',
  TimedOut = 'timed_out',
  Superseded = 'superseded',
}

/** Who authored a chat message. */
export enum AssistantMessageRole {
  User = 'user',
  Assistant = 'assistant',
}

/**
 * How an assistant reply wants to be shown (Increment 2). The model tags its terminal turn via the
 * backend `setPresentation` tool. `answer` = a short glanceable fact → the transient HUD; `list` = a
 * pick-one palette (see `IAssistantPresentationItem`); `conversation` (default/absent) = the resizable
 * dock. Additive: an untagged reply renders in the dock as before.
 */
export type AssistantPresentation = 'answer' | 'conversation' | 'list'

/**
 * A pickable option in a `list` reply. `label` is shown; picking it sends `prompt` as the user's next
 * message (so a pick routes back through the normal send path — no client-built routes).
 */
export interface IAssistantPresentationItem {
  label: string
  prompt: string
}

/**
 * A single chat message in the assistant stream.
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md (field names: id, role, text, error, muted, createdAt)
 */
export interface IAssistantMessage extends Partial<ITimestamps> {
  id: string
  role: AssistantMessageRole
  text: string
  /** Renders the bubble body in the error color (design-contract.md → §Surface 2). */
  error?: boolean
  /** Renders the bubble body muted/subdued (design-contract.md → §Surface 2). */
  muted?: boolean
  createdAt?: Date
  /** Owner keys the live listener filters on so the owner firestore rule allows the read. */
  authUid?: string
  customerKey?: string
  /** Scopes the top-level `assistant_messages` query to a single conversation. */
  conversationId?: string
  /**
   * Increment 2 — the reply's chosen surface (assistant messages only). `answer` pops the transient HUD;
   * `list` shows a pick-one palette; `conversation`/absent renders in the dock. Flows straight from the
   * message doc via the repository.
   */
  presentation?: AssistantPresentation
  /** The options for a `list` reply. Picking one sends its `prompt` as the user's next message. */
  presentationItems?: IAssistantPresentationItem[]
}

/**
 * A single run-step row interleaved into the assistant stream.
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md (field names: id, label, detail, status)
 */
export interface IAssistantRunStep extends Partial<ITimestamps> {
  id: string
  label: string
  /** Optional markdown detail rendered under the label. */
  detail?: string
  status: RunStepStatus
  /** Owner keys the live step listener filters on so the owner firestore rule allows the read. */
  authUid?: string
  customerKey?: string
  /**
   * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
   * Present only on a `client_tool_call` step (story 4.9): the client tool to invoke + its args. Its
   * presence is how the executor detects a tool step off `steps$`. Additive/optional — existing
   * `{ id, label, detail?, status }` usage is unaffected.
   */
  clientTool?: IClientToolCall
}

/**
 * The live assistant conversation document backing the panel.
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md (field names: status, messages, steps)
 */
export interface IAssistantConversation extends HasId<string>, Partial<ITimestamps> {
  status: ConversationStatus
  messages: IAssistantMessage[]
  steps: IAssistantRunStep[]
  // A one-way navigation hint the backend sets after a run whose write named a screen. The panel navigates once
  // per `runId` and never writes it back; a stale value is simply ignored once it has been acted on.
  pendingNavigation?: { route: string; runId: string }
}

/**
 * The composer may send only when the conversation is idle. Any non-idle status (running,
 * awaiting confirmation, error) disables send (CAP-3). Pure function — unit-tested.
 */
export function canSend(status: ConversationStatus): boolean {
  return status === ConversationStatus.Idle
}

/** A status pill descriptor for a run-step row. */
export interface RunStepTag {
  color: Color
  label: string
}

/**
 * Maps a run-step status to its `e11-tag` color + English-as-key label
 * (design-contract.md → §Surface 2). Unknown/extra statuses fall back to a neutral `info` pill.
 */
export function runStepStatusToTag(status: RunStepStatus | string): RunStepTag {
  switch (status) {
    case RunStepStatus.Running:
      return { color: 'primary', label: 'Running' }
    case RunStepStatus.AwaitingClient:
      return { color: 'warning', label: 'Working' }
    case RunStepStatus.Completed:
      return { color: 'success', label: 'Done' }
    case RunStepStatus.Failed:
      return { color: 'error', label: 'Failed' }
    case RunStepStatus.TimedOut:
      return { color: 'error', label: 'Timed out' }
    case RunStepStatus.Superseded:
      return { color: 'info', label: 'Superseded' }
    default:
      return { color: 'info', label: 'Working' }
  }
}
